import { useMemo, useState } from "react";
import { useRole } from "@/data/RoleContext";
import { usePermissions } from "@/data/PermissionsContext";
import { PermissionGate } from "@/components/PermissionGate";
import type { UnifiedDocument, DocumentCategory, DocumentStatus } from "@/data/documentTypes";
import type { Opportunity } from "@/data/opportunityTypes";
import { useAppData } from "@/data/AppDataContext";
import { OpportunityReadOnlySummary } from "@/components/OpportunityReadOnlySummary";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  FileText, FileCheck, Camera, AlertTriangle, Search, Download,
  Lock, Eye, Archive, Send, PenTool, File, ExternalLink,
} from "lucide-react";
import { createSignedStorageFullImageUrl } from "@/lib/storageImageUrls";
import { toast } from "sonner";
import { type UseUserResolverReturn } from "@/hooks/useUserResolver";
import { jsPDF } from "jspdf";

function looksLikeUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
}

function displayActor(raw: string, resolved?: string): string {
  const candidate = (resolved || raw || "").trim();
  if (!candidate) return "System";
  return looksLikeUuid(candidate) ? "System" : candidate;
}

const CATEGORY_ICONS: Record<DocumentCategory, React.ElementType> = {
  Quote: FileText,
  Contract: FileCheck,
  "Change Order": PenTool,
  Disposition: File,
  "QA Photo": Camera,
  "Issue Photo": AlertTriangle,
  NTBO: FileText,
  Other: File,
};

const STATUS_STYLES: Record<DocumentStatus, string> = {
  Draft: "bg-muted text-muted-foreground border-border",
  Sent: "bg-info/10 text-info border-info/30",
  Viewed: "bg-accent/10 text-accent-foreground border-accent/30",
  Signed: "bg-success/10 text-success border-success/30",
  Archived: "bg-muted text-muted-foreground border-border",
};

const STATUS_ICONS: Record<DocumentStatus, React.ElementType> = {
  Draft: File,
  Sent: Send,
  Viewed: Eye,
  Signed: FileCheck,
  Archived: Archive,
};

interface Props {
  documents: UnifiedDocument[];
  title?: string;
  compact?: boolean;
  userResolver?: UseUserResolverReturn;
}

export function DocumentCenter({ documents, title = "Documents", compact, userResolver }: Props) {
  const { role } = useRole();
  const { hasPermission: hasPerm } = usePermissions();
  const { quotes, jobs } = useAppData();
  const canDownload = hasPerm("reports_export");
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState<DocumentCategory | "All">("All");
  const [previewDoc, setPreviewDoc] = useState<UnifiedDocument | null>(null);

  const categories = useMemo(() => {
    const cats = new Set(documents.map(d => d.category));
    return ["All" as const, ...Array.from(cats)];
  }, [documents]);

  const filtered = useMemo(() => {
    return documents.filter(d => {
      if (filterCategory !== "All" && d.category !== filterCategory) return false;
      if (search && !d.title.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [documents, filterCategory, search]);

  const downloadDocumentPdf = (doc: UnifiedDocument) => {
    const pdf = new jsPDF();
    let y = 18;

    const writeLine = (label: string, value: string) => {
      pdf.setFont("helvetica", "bold");
      pdf.text(`${label}:`, 14, y);
      pdf.setFont("helvetica", "normal");
      const lines = pdf.splitTextToSize(value || "—", 155);
      pdf.text(lines, 42, y);
      y += Math.max(6, lines.length * 5 + 1);
    };

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    pdf.text(doc.title || "Document", 14, y);
    y += 9;
    pdf.setFontSize(10);

    writeLine("Category", doc.category);
    writeLine("Status", doc.status);
    writeLine("Customer", doc.customerName || "—");
    writeLine("Created", doc.createdAt ? new Date(doc.createdAt).toLocaleString() : "—");
    writeLine("Updated", doc.updatedAt ? new Date(doc.updatedAt).toLocaleString() : "—");
    writeLine("Created by", displayActor(doc.createdBy, userResolver?.getName(doc.createdBy)));
    if (doc.jobId) writeLine("Job Ref", doc.jobId);
    if (doc.quoteId) writeLine("Quote Ref", doc.quoteId);
    if (doc.notes) writeLine("Notes", doc.notes);
    if (doc.contentRef) writeLine("Source Link", doc.contentRef);

    const safeTitle = (doc.title || "document")
      .replace(/[^a-z0-9-_ ]/gi, "")
      .trim()
      .replace(/\s+/g, "_")
      .toLowerCase();
    pdf.save(`${safeTitle || "document"}_${doc.id}.pdf`);
  };

  const previewOpportunity = useMemo<Opportunity | null>(() => {
    if (!previewDoc) return null;
    const quoteId = String(previewDoc.quoteId ?? "").trim();
    if (quoteId) {
      return (quotes as Opportunity[]).find((q) => q.id === quoteId) ?? null;
    }
    const jobId = String(previewDoc.jobId ?? "").trim();
    if (!jobId) return null;
    const linked = (quotes as Opportunity[]).find((q) => q.linkedJobId === jobId || q.convertedJobId === jobId);
    return linked ?? null;
  }, [previewDoc, quotes]);

  const previewNoJobReason = useMemo<"rescission" | "canceled" | "finance_declined" | "no_link" | undefined>(() => {
    if (!previewOpportunity || previewOpportunity.stage !== "Closed Won") return undefined;
    if (previewOpportunity.wonOutcome === "canceled") return "canceled";
    if (previewOpportunity.wonOutcome === "finance_declined") return "finance_declined";
    if (!previewOpportunity.linkedJobId) return "no_link";
    const linked = jobs.find((j) => j.id === previewOpportunity.linkedJobId || j.jobNumber === previewOpportunity.linkedJobId);
    return linked ? undefined : "no_link";
  }, [previewOpportunity, jobs]);

  const downloadPreviewPdf = () => {
    if (!previewDoc) return;
    if (!previewOpportunity) {
      downloadDocumentPdf(previewDoc);
      return;
    }

    const pdf = new jsPDF("p", "pt", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 28;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    const addSectionTitle = (label: string) => {
      if (y > 760) {
        pdf.addPage();
        y = margin;
      }
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.text(label.toUpperCase(), margin, y);
      y += 12;
      pdf.setDrawColor(210);
      pdf.line(margin, y, margin + contentWidth, y);
      y += 10;
    };

    const addField = (label: string, value: string) => {
      if (y > 780) {
        pdf.addPage();
        y = margin;
      }
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.text(`${label}:`, margin, y);
      pdf.setFont("helvetica", "normal");
      const lines = pdf.splitTextToSize(value || "—", contentWidth - 110);
      pdf.text(lines, margin + 110, y);
      y += Math.max(14, lines.length * 11);
    };

    const readNum = (v: unknown): number | null => {
      if (typeof v === "number" && Number.isFinite(v)) return v;
      if (typeof v === "string" && v.trim()) {
        const parsed = Number(v);
        return Number.isFinite(parsed) ? parsed : null;
      }
      return null;
    };
    const readStr = (v: unknown): string => (typeof v === "string" ? v.trim() : "");
    const asObj = (v: unknown): Record<string, unknown> | null => (
      v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null
    );

    const customerName = previewOpportunity.customerName || previewOpportunity.primaryName || "Unnamed";
    const fullAddress = previewOpportunity.customerAddress ||
      [previewOpportunity.address, previewOpportunity.city, [previewOpportunity.state, previewOpportunity.zip].filter(Boolean).join(" ").trim()]
        .filter(Boolean)
        .join(", ");
    const config = asObj(previewOpportunity.projectConfig);
    const pricing = asObj(config?.pricing);
    const subtotal = readNum((previewOpportunity as unknown as { subtotal?: unknown }).subtotal) ?? readNum(pricing?.subtotal) ?? readNum(config?.subtotal);
    const tax = readNum((previewOpportunity as unknown as { tax?: unknown }).tax) ?? readNum(pricing?.tax) ?? readNum(config?.tax);
    const discount = readNum((previewOpportunity as unknown as { discount?: unknown }).discount) ?? readNum(pricing?.discount) ?? readNum(config?.discount);
    const total = readNum((previewOpportunity as unknown as { total?: unknown }).total) ?? readNum(pricing?.total) ?? (subtotal != null ? subtotal + (tax ?? 0) - (discount ?? 0) : null) ?? readNum(previewOpportunity.totalEstimate);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.text(previewDoc.title || "Opportunity Snapshot", margin, y);
    y += 18;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, y);
    y += 16;

    addSectionTitle("Header");
    addField("Reference", previewOpportunity.displayReference || previewOpportunity.id);
    addField("Stage", previewOpportunity.stage);
    addField("Customer", customerName);
    addField("Project Type", previewOpportunity.projectType || "—");
    addField("Lead Source", previewOpportunity.leadSource || "—");
    addField("Assigned Rep", previewOpportunity.assignedRep || "Unassigned");

    addSectionTitle("Outcome");
    addField("Won At", previewOpportunity.wonAt ? new Date(previewOpportunity.wonAt).toLocaleDateString() : "—");
    addField("Won Outcome", previewOpportunity.wonOutcome || "—");
    addField("Lost At", previewOpportunity.lostAt ? new Date(previewOpportunity.lostAt).toLocaleDateString() : "—");
    addField("Lost Reason", previewOpportunity.lostReason || "—");
    addField("Linked Job", previewOpportunity.linkedJobId || previewOpportunity.convertedJobId || "—");
    addField("No Active Job Reason", previewNoJobReason || "—");

    addSectionTitle("Contact");
    addField("Primary Phone", previewOpportunity.primaryPhone || previewOpportunity.customerPhone || "—");
    addField("Primary Email", previewOpportunity.primaryEmail || previewOpportunity.customerEmail || "—");
    addField("Address", fullAddress || "—");

    addSectionTitle("Price Breakdown");
    addField("Subtotal", subtotal != null ? `$${subtotal.toLocaleString()}` : "—");
    addField("Tax", tax != null ? `$${tax.toLocaleString()}` : "—");
    addField("Discount", discount != null ? `$${discount.toLocaleString()}` : "—");
    addField("Total", total != null ? `$${total.toLocaleString()}` : "—");

    addSectionTitle("Project Configuration");
    addField("Category", readStr(config?.projectCategory) || readStr(config?.consultationCategory) || readStr(config?.autoProjectType) || "—");
    addField("Build Type", readStr(config?.selectedBuildType) || readStr(config?.selectedSystemType) || readStr(config?.selectedShowerType) || "—");
    addField("Dimensions", readStr(config?.selectedDimensions) || readStr(config?.selectedSize) || readStr(config?.dimensions) || "—");
    addField("Wall/Material", readStr(config?.selectedWallColor) || readStr(config?.selectedWallType) || readStr(config?.selectedMaterial) || "—");
    addField("Base/Pan", readStr(config?.selectedBaseType) || readStr(config?.selectedPanType) || readStr(config?.selectedBase) || "—");
    addField("Glass/Enclosure", readStr(config?.selectedDoorType) || readStr(config?.selectedGlassType) || readStr(config?.selectedEnclosure) || "—");
    addField("Scope of Work", previewOpportunity.scopeOfWork || "—");

    const safeTitle = (previewDoc.title || "document")
      .replace(/[^a-z0-9-_ ]/gi, "")
      .trim()
      .replace(/\s+/g, "_")
      .toLowerCase();
    pdf.save(`${safeTitle || "document"}_${previewDoc.id}.pdf`);
  };

  const openStorageFile = async (storageRef: string) => {
    const sepIdx = storageRef.indexOf("::");
    if (sepIdx === -1) {
      toast.error("Invalid file reference.");
      return;
    }
    const bucket = storageRef.slice(0, sepIdx);
    const path = storageRef.slice(sepIdx + 2);
    const signedUrl = await createSignedStorageFullImageUrl(path, {
      bucket,
      expiresIn: 3600,
    }).catch(() => "");
    if (!signedUrl) {
      toast.error("Could not open the contract file. It may have been moved or deleted.");
      return;
    }
    window.open(signedUrl, "_blank", "noopener,noreferrer");
  };

  const handleDocumentAction = (doc: UnifiedDocument) => {
    if (doc.category === "Quote" || doc.category === "Contract") {
      setPreviewDoc(doc);
      return;
    }
    downloadDocumentPdf(doc);
  };

  if (documents.length === 0 && compact) return null;

  return (
    <div className="space-y-3">
      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search documents..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8 h-8 text-xs"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {categories.map(cat => (
            <Button
              key={cat}
              variant={filterCategory === cat ? "default" : "outline"}
              size="sm"
              className="text-[10px] h-7 px-2"
              onClick={() => setFilterCategory(cat)}
            >
              {cat}
            </Button>
          ))}
        </div>
      </div>

      {/* Document list */}
      {filtered.length === 0 ? (
        <p className="text-xs text-muted-foreground italic text-center py-4">No documents found.</p>
      ) : (
        <div className="space-y-1.5">
          {filtered.map(doc => {
            const Icon = CATEGORY_ICONS[doc.category] || File;
            const StatusIcon = STATUS_ICONS[doc.status] || File;
            return (
              <div
                key={doc.id}
                className="flex items-center justify-between rounded-lg border bg-card px-3 py-2.5 hover:bg-muted/20 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-1.5 rounded bg-primary/10 shrink-0">
                    <Icon className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold">{doc.title}</span>
                      {doc.version > 1 && (
                        <Badge variant="outline" className="text-[8px] h-4">v{doc.version}</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground">
                      <span>{doc.category}</span>
                      <span>·</span>
                      <span>{new Date(doc.createdAt).toLocaleDateString()}</span>
                      <span>·</span>
                      <span>{displayActor(doc.createdBy, userResolver?.getName(doc.createdBy))}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="outline" className={`text-[9px] gap-1 ${STATUS_STYLES[doc.status]}`}>
                    <StatusIcon className="h-2.5 w-2.5" />
                    {doc.status}
                  </Badge>
                  {doc.storageRef && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      title="Open contract file"
                      onClick={() => openStorageFile(doc.storageRef!)}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {canDownload ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      title="Download PDF"
                      onClick={() => handleDocumentAction(doc)}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                  ) : (
                    <span title="Download restricted"><Lock className="h-3.5 w-3.5 text-muted-foreground" /></span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary */}
      <div className="flex items-center justify-between pt-2 border-t text-[10px] text-muted-foreground">
        <span>{filtered.length} document{filtered.length !== 1 ? "s" : ""}</span>
        {!canDownload && (
          <span className="flex items-center gap-1">
            <Lock className="h-3 w-3" /> Downloads restricted to admin roles
          </span>
        )}
      </div>

      <Dialog open={!!previewDoc} onOpenChange={(open) => !open && setPreviewDoc(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{previewDoc?.title || "Document Preview"}</DialogTitle>
          </DialogHeader>
          {previewDoc && (
            <div className="space-y-3">
              <div className="max-h-[70vh] overflow-auto rounded-md border p-2 bg-background">
                {previewOpportunity ? (
                  <OpportunityReadOnlySummary
                    opportunity={previewOpportunity}
                    noJobReason={previewNoJobReason}
                    embedded
                  />
                ) : (
                  <div className="space-y-3 p-2">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="rounded border p-2"><span className="text-muted-foreground">Category</span><p className="font-medium mt-1">{previewDoc.category}</p></div>
                      <div className="rounded border p-2"><span className="text-muted-foreground">Status</span><p className="font-medium mt-1">{previewDoc.status}</p></div>
                      <div className="rounded border p-2"><span className="text-muted-foreground">Customer</span><p className="font-medium mt-1">{previewDoc.customerName || "—"}</p></div>
                      <div className="rounded border p-2"><span className="text-muted-foreground">Created By</span><p className="font-medium mt-1">{displayActor(previewDoc.createdBy, userResolver?.getName(previewDoc.createdBy))}</p></div>
                      <div className="rounded border p-2"><span className="text-muted-foreground">Created</span><p className="font-medium mt-1">{previewDoc.createdAt ? new Date(previewDoc.createdAt).toLocaleString() : "—"}</p></div>
                      <div className="rounded border p-2"><span className="text-muted-foreground">Updated</span><p className="font-medium mt-1">{previewDoc.updatedAt ? new Date(previewDoc.updatedAt).toLocaleString() : "—"}</p></div>
                    </div>
                    {previewDoc.notes && (
                      <div className="rounded border p-2">
                        <p className="text-xs text-muted-foreground">Notes</p>
                        <p className="text-sm mt-1 whitespace-pre-wrap">{previewDoc.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end gap-2">
                {previewDoc.storageRef && (
                  <PermissionGate permission="reports_export">
                    <Button size="sm" variant="outline" onClick={() => openStorageFile(previewDoc.storageRef!)}>
                      <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open Contract File
                    </Button>
                  </PermissionGate>
                )}
                <PermissionGate permission="reports_export">
                  <Button size="sm" onClick={downloadPreviewPdf}>
                    <Download className="h-3.5 w-3.5 mr-1" /> Download PDF
                  </Button>
                </PermissionGate>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
